/**
 * The two outboxes (wave COM items 4 and 5), read by the tenant's power user only: GET
 * /api/email-outbox and GET /api/webhook-outbox, each newest first, with the count of
 * every status and the server's paging. Anyone else is answered absence (404).
 *
 * Author: Mahmoud Ragab Ahmed.
 */

import { apiJson, isRecord, num, optNum, optStr, query, str } from "./client";

export const EMAIL_STATUSES = ["PENDING", "SENT", "SUPPRESSED", "FAILED"] as const;
export const WEBHOOK_STATUSES = ["PENDING", "DELIVERED", "DEAD", "FAILED"] as const;

export type EmailStatus = (typeof EMAIL_STATUSES)[number];
export type WebhookStatus = (typeof WEBHOOK_STATUSES)[number];

export interface EmailOutboxRow {
  readonly id: string;
  readonly recipientDisplayName: string;
  readonly template: string;
  readonly message: string;
  readonly deepLink: string;
  readonly status: string;
  readonly suppressedReason: string | null;
  readonly transport: string | null;
  readonly attempts: number;
  readonly nextAttemptAt: string;
  readonly lastError: string | null;
  readonly createdAt: string;
  readonly sentAt: string | null;
}

export interface WebhookOutboxRow {
  readonly id: string;
  readonly endpointId: string;
  readonly endpointUrl: string;
  readonly eventKind: string;
  readonly status: string;
  readonly attempts: number;
  readonly nextAttemptAt: string;
  readonly lastStatusCode: number | null;
  readonly lastError: string | null;
  readonly createdAt: string;
  readonly deliveredAt: string | null;
}

export interface OutboxPage<T> {
  /** The rows behind the status filter, across every page. */
  readonly total: number;
  /** The count of each status over the whole outbox, the filter aside. */
  readonly counts: Readonly<Record<string, number>>;
  readonly items: readonly T[];
}

function counts(raw: unknown): Record<string, number> {
  if (!isRecord(raw)) {
    return {};
  }
  return Object.fromEntries(
    Object.entries(raw).filter((entry): entry is [string, number] => typeof entry[1] === "number"),
  );
}

function page<T>(raw: unknown, row: (body: Record<string, unknown>) => T): OutboxPage<T> {
  if (!isRecord(raw) || !Array.isArray(raw.items)) {
    throw new Error("Expected an outbox list body");
  }
  return {
    total: num(raw.total),
    counts: counts(raw.counts),
    items: raw.items.filter(isRecord).map(row),
  };
}

function emailRow(body: Record<string, unknown>): EmailOutboxRow {
  return {
    id: str(body.id),
    recipientDisplayName: str(body.recipient_display_name),
    template: str(body.template),
    message: str(body.message),
    deepLink: str(body.deep_link),
    status: str(body.status),
    suppressedReason: optStr(body.suppressed_reason),
    transport: optStr(body.transport),
    attempts: num(body.attempts),
    nextAttemptAt: str(body.next_attempt_at),
    lastError: optStr(body.last_error),
    createdAt: str(body.created_at),
    sentAt: optStr(body.sent_at),
  };
}

function webhookRow(body: Record<string, unknown>): WebhookOutboxRow {
  return {
    id: str(body.id),
    endpointId: str(body.endpoint_id),
    endpointUrl: str(body.endpoint_url),
    eventKind: str(body.event_kind),
    status: str(body.status),
    attempts: num(body.attempts),
    nextAttemptAt: str(body.next_attempt_at),
    lastStatusCode: optNum(body.last_status_code),
    lastError: optStr(body.last_error),
    createdAt: str(body.created_at),
    deliveredAt: optStr(body.delivered_at),
  };
}

export interface OutboxFilter {
  readonly status: string | null;
  readonly limit: number;
  readonly offset: number;
  readonly signal?: AbortSignal | undefined;
}

export async function listEmailOutbox(
  token: string,
  filter: OutboxFilter,
): Promise<OutboxPage<EmailOutboxRow>> {
  return apiJson(
    token,
    `/email-outbox${query({ status: filter.status, limit: filter.limit, offset: filter.offset })}`,
    (raw) => page(raw, emailRow),
    { signal: filter.signal },
  );
}

export async function listWebhookOutbox(
  token: string,
  filter: OutboxFilter,
): Promise<OutboxPage<WebhookOutboxRow>> {
  return apiJson(
    token,
    `/webhook-outbox${query({ status: filter.status, limit: filter.limit, offset: filter.offset })}`,
    (raw) => page(raw, webhookRow),
    { signal: filter.signal },
  );
}
